import { useState } from 'react';
import { useSelector } from 'react-redux';
import { EntityTypes } from '../../../new-lib/constants/common';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import { Compound } from '../../../new-lib/types/entity';
import { getImages } from '../../../helpers/common';
import EntityLayout from '../components/layout/EntityLayout';
import StickyDetails from '../components/entity-details/StickyDetails';
import EntityDetails from '../components/entity-details/EntityDetails';
import NawyImageGallery from '../../../new-components/NawyImageGallery';
import SuggestedContainer from '../../../new-components/SuggestedContainer';
import Backlinks from '../components/Backlinks';
import ContactFormContainer from '../../contact/components/organic-form/ContactFormContainer';
import Description from '../components/Description';
import Amenities from '../components/Amenities';
import CompoundSlider from '../components/CompoundsSlider';
import QuickView from '../../../new-components/display-system/QuickView';
import RequestMeetingBtn from '../../contact/components/request-meeting/RequestMeetingBtn';
import RequestMeetingContainer from '../../contact/components/request-meeting/RequestMeetingContainer';
import { logEvent } from '../../../new-lib/analytics';
import FormHeader from '../../contact/components/organic-form/FormHeader';
import SoldOutSection from '../components/SoldOutSection';
import FulfilledByTaskeen from '../components/FulfilledByTaskeen';
import LaunchesBannerContainer from '../launch/components/LaunchesBanner/LaunchesBannerContainer';
import ExplorePropertiesContainer from '../components/explore-properties/ExplorePropertiesContainer';
import PaymentPlansContainer from './PaymentPlansContainer';
import FAQs from '../components/FAQs';
import { FAQ } from '../../../new-lib/types/common';
import { CompoundLaunchDto } from '../../../new-lib/dtos/compounds/compound-launch.dto';

interface CompoundViewProps {
  compound: Compound;
  availablePropertyTypes: [];
  hasAvailability: boolean;
  filterOptions: [];
  launches: CompoundLaunchDto[];
  showOffers: boolean;
  breadcrumbs: React.ReactElement;
  recommendedCompounds: [];
  faqs: FAQ[];
}

const CompoundView = ({
  compound,
  availablePropertyTypes,
  hasAvailability,
  filterOptions,
  launches,
  showOffers,
  breadcrumbs,
  recommendedCompounds,
  faqs
}: CompoundViewProps) => {
  const eventId = useSelector((state: any) => state.app.eventId);
  const [meetingForm, setMeetingForm] = useState<boolean>(false);
  const images = getImages(compound?.images);
  const handleRequestMeetingClick = () => {
    logEvent.contact('form_physical_meeting', `CompoundPage`, eventId, {
      entity_type: EntityTypes.COMPOUND,
      cmpnd_id: compound.id,
      cmpnd_name: compound.name,
      org_id: compound.nawyOrganizationId ?? 1
    });
    setMeetingForm(true);
  };

  if (images.length == 1) images.push(images[0]);
  return (
    <EntityLayout>
      {breadcrumbs}

      {compound.nawyOrganizationId === 2 && <FulfilledByTaskeen />}

      {images && images.length > 0 && (
        <div className="gallery-container">
          <NawyImageGallery
            entity={compound}
            type={EntityTypes.COMPOUND}
            images={images.slice(0, images.length - 1)}
            fixedImage={images[images.length - 1]}
          />
        </div>
      )}

      <div className="main-container" data-test="compound-details">
        <StickyDetails
          entity={compound}
          entityType={
            compound.isSuper ? EntityTypes.SUPER_COMPOUND : EntityTypes.COMPOUND
          }
          contactEventExtraParams={{
            entity_type: compound.isSuper
              ? EntityTypes.SUPER_COMPOUND
              : EntityTypes.COMPOUND,
            cmpnd_id: compound.id,
            cmpnd_name: compound.name,
            org_id: compound.nawyOrganizationId ?? 1,
            min_price: compound.prices?.minPrice,
            max_price: compound.prices?.maxPrice,
            dev_price: compound.prices?.developerStartingPrice,
            resale_price: compound.prices?.resaleStartingPrice,
            area_id: compound?.areaId,
            area_name: compound?.areaName,
            dev_id: compound?.developerId,
            dev_name: compound?.developerName
          }}
        />
        <EntityDetails
          entityType={EntityTypes.COMPOUND}
          galleryImages={compound.images}
          masterPlanImage={compound.masterPlan}
          mapData={{
            lat: compound.lat,
            long: compound.long,
            id: compound?.id,
            image: compound?.coverImageUrl,
            isFiltered: true,
            name: compound.name,
            geometry: compound?.geometry,
            coordinates: compound?.coordinates,
            parent_id: compound?.parentCompound?.id
          }}
        />
        {hasAvailability && <LaunchesBannerContainer launches={launches} />}
        <PaymentPlansContainer
          entityType={EntityTypes.COMPOUND}
          paymentPlans={compound.paymentPlans}
          showOffers={showOffers}
          offers={compound.offers}
        />
        {hasAvailability && (
          <ExplorePropertiesContainer
            headline="explore_properties_in"
            entityDetails={{
              type: SearchFiltersTypes.COMPOUNDS,
              id: compound.id,
              name: compound.name,
              resale_min_price: compound.prices?.resaleStartingPrice
            }}
            filterOptions={filterOptions}
            exploreLocation="ExplorePropertiesSection"
          />
        )}
        <div className="entity-layout compound-layout">
          <div className="details-side">
            {!hasAvailability && (
              <>
                <LaunchesBannerContainer launches={launches} />
                <SoldOutSection
                  recommendedCompounds={recommendedCompounds}
                  compound={{
                    id: compound.id,
                    name: compound.name,
                    nawy_organization_id: compound.nawyOrganizationId ?? null,
                    area: {
                      id: String(compound.areaId),
                      name: compound.areaName
                    }
                  }}
                />
              </>
            )}
            {compound.amenities.length > 0 && (
              <Amenities amenities={compound.amenities} />
            )}

            {compound.description && (
              <Description
                headtxt={compound.name}
                description={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: compound.description
                    }}
                  />
                }
              />
            )}

            {faqs?.length > 0 && (
              <FAQs
                faqs={faqs}
                id={compound.id}
                name={compound.name}
                pageLocation="CompoundPage"
              />
            )}
          </div>
          {hasAvailability && (
            <div className="banners-side">
              <div className="form-container">
                <div className="form">
                  <ContactFormContainer
                    renderedInputs={{
                      name: true,
                      location: true,
                      message: true,
                      mobileNumber: true
                    }}
                    layout={{ header: <FormHeader hasIcon={true} /> }}
                    organizationId={compound.nawyOrganizationId ?? null}
                    compound={{
                      id: compound.id,
                      name: compound.name,
                      nawy_organization_id:
                        compound?.nawyOrganizationId ?? null,
                      area: {
                        id: String(compound.areaId),
                        name: compound.areaName
                      },
                      developer: {
                        id: String(compound.developerId),
                        name: compound.developerName
                      },
                      min_price: compound.prices?.minPrice,
                      max_price: compound.prices?.maxPrice,
                      dev_price: compound.prices?.developerStartingPrice
                    }}
                  />
                </div>
                <RequestMeetingBtn clickHandler={handleRequestMeetingClick} />
                {availablePropertyTypes.length > 0 && (
                  <Backlinks
                    compoundDetails={{
                      name: compound.name,
                      slug: compound.slug
                    }}
                    availablePropertyTypes={availablePropertyTypes}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {compound.isSuper && compound.phases?.length > 0 && (
        <CompoundSlider
          entityCompounds={compound.phases}
          sliderLocation="CompoundsInCompoundPage"
          imageSize="medium"
        />
      )}
      {hasAvailability && (
        <SuggestedContainer suggestedCompounds={recommendedCompounds} />
      )}

      <QuickView isOpen={meetingForm} setIsOpen={setMeetingForm}>
        <RequestMeetingContainer
          location={{ compoundId: compound.id.toString() }}
          setFormOpen={setMeetingForm}
        />
      </QuickView>
    </EntityLayout>
  );
};

export default CompoundView;
